import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpenIcon, ChevronRightIcon, LogInIcon } from "lucide-react";
import { useCallback } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { userMistakesInfiniteQueryOptions } from "@/hooks/query/thread";
import { useDebounce } from "@/hooks/use-debounce";
import { noIndexSeo } from "@/lib/seo";
import * as m from "@/paraglide/messages";

import { AppLayout } from "../-components/app-layout";
import type { BlockTypeFilter } from "./-components/mistakes/mistake-filter-utils";
import { MistakeList } from "./-components/mistakes/mistake-list";
import { MistakeSearchBar } from "./-components/mistakes/mistake-search-bar";
import { MistakeStats } from "./-components/mistakes/mistake-stats";

const searchSchema = z.object({
	q: z.string().optional().default(""),
	type: z.string().optional(),
});

export const Route = createFileRoute("/learn/mistakes")({
	component: MistakesPage,
	validateSearch: searchSchema,
	head: ({ match }) => {
		return noIndexSeo({
			title: m.learn_mistakesTitle(),
			description: m.learn_mistakesDescription(),
			pathname: match.pathname,
		});
	},
	loader: async ({ context }) => {
		return {
			user: context.user,
			s: context.user,
		};
	},
});

function MistakesPage() {
	const { user } = Route.useLoaderData() || {};
	const isLoggedIn = !!user;
	const navigate = Route.useNavigate();

	// URL-synced search state
	const { q: keyword, type: typeFilter } = Route.useSearch();
	const debouncedKeyword = useDebounce(keyword, 300);

	const setKeyword = useCallback(
		(value: string) => {
			navigate({
				search: (prev) => ({ ...prev, q: value || undefined }),
				replace: true,
			});
		},
		[navigate],
	);

	const setTypeFilter = useCallback(
		(value: BlockTypeFilter | null) => {
			navigate({
				search: (prev) => ({ ...prev, type: value || undefined }),
				replace: true,
			});
		},
		[navigate],
	);

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useInfiniteQuery(
		userMistakesInfiniteQueryOptions({
			keyword: debouncedKeyword || undefined,
			blockType: typeFilter || undefined,
			enabled: isLoggedIn,
		}),
	);

	const pages = data?.pages ?? [];
	const mistakesByVersion = pages.flatMap((page) => page.data);
	const totalCount = pages[0]?.totalCount ?? 0;
	// Count unique versions across all pages
	const sessionCount = new Set(mistakesByVersion.map((g) => g.versionId)).size;

	const hasFilters = !!debouncedKeyword || !!typeFilter;
	const showEmptyState = !isPending && isLoggedIn && totalCount === 0 && !hasFilters;

	return (
		<AppLayout>
			<div className="flex h-full flex-col">
				{/* Header */}
				<div className="shrink-0 border-b bg-background px-6 pt-6 pb-4">
					<div className="mx-auto w-full max-w-3xl">
						<nav
							aria-label="Breadcrumb"
							className="flex items-center gap-1 text-sm text-muted-foreground"
						>
							<Link
								to="/learn"
								className="rounded-sm px-1 py-0.5 transition-colors hover:text-foreground"
							>
								{m.learn_heading()}
							</Link>
							<ChevronRightIcon className="size-4 shrink-0" />
							<span aria-current="page" className="px-1 py-0.5 text-foreground">
								{m.learn_mistakesTitle()}
							</span>
						</nav>
					</div>
				</div>

				{/* Content */}
				<div className="flex-1 overflow-y-auto p-6">
					<div className="mx-auto w-full max-w-3xl space-y-6">
						{!isLoggedIn ? (
							<NotLoggedIn />
						) : showEmptyState ? (
							<EmptyMistakes />
						) : (
							<>
								<MistakeStats
									totalMistakes={totalCount}
									sessionCount={sessionCount}
									loading={isPending}
								/>
								<MistakeSearchBar
									keyword={keyword}
									onKeywordChange={setKeyword}
									typeFilter={(typeFilter as BlockTypeFilter) ?? null}
									onTypeFilterChange={setTypeFilter}
								/>
								{isPending ? (
									<ListSkeleton />
								) : (
									<MistakeList
										mistakesByVersion={mistakesByVersion}
										hasNextPage={!!hasNextPage}
										isFetchingNextPage={isFetchingNextPage}
										onLoadMore={fetchNextPage}
									/>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</AppLayout>
	);
}

function ListSkeleton() {
	return (
		<div className="space-y-4">
			<Skeleton className="h-48 rounded-2xl" />
			<Skeleton className="h-48 rounded-2xl" />
		</div>
	);
}

function EmptyMistakes() {
	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<BookOpenIcon />
				</EmptyMedia>
				<EmptyTitle>{m.learn_mistakesEmpty()}</EmptyTitle>
				<EmptyDescription>{m.learn_mistakesEmptyDescription()}</EmptyDescription>
			</EmptyHeader>
		</Empty>
	);
}

function NotLoggedIn() {
	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<LogInIcon />
				</EmptyMedia>
				<EmptyTitle>{m.learn_signInTitle()}</EmptyTitle>
				<EmptyDescription>{m.learn_signInDescription()}</EmptyDescription>
			</EmptyHeader>
			<Button nativeButton={false} render={<Link to="/sign-in" />}>
				{m.learn_signIn()}
			</Button>
		</Empty>
	);
}
